/* eslint-disable no-param-reassign */
import axios from 'axios';
import Bugsnag from '@bugsnag/js'
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import _ from 'lodash';
import { GATEWAY_URL } from './constants';
import { getToken, login, logout, TOKEN_KEY } from './auth';

const UNKNOWN_REQUEST_ERROR_MESSAGE = 'Falha desconhecida na requisição';

const endpointsTimeout = {};
let timeout;

const gateway = axios.create({
  baseURL: GATEWAY_URL
});

const refreshAuthLogic = failedRequest => {
  if (failedRequest.response.config.headers.Authorization) {
    return gateway
      .post(`gateway/token/refresh`)
      .then(tokenRefreshResponse => {
        localStorage.setItem(TOKEN_KEY, tokenRefreshResponse.token);
        document.location.reload(true);
        failedRequest.response.config.headers.Authorization = `Bearer ${tokenRefreshResponse.token}`;
        return Promise.resolve();
      })
      .catch((err) => {
        console.log('refreshAuthLogic', err);  
        localStorage.clear();
        window.location.href = window.location.origin;
        return Promise.reject(err);
      });
  }
  return Promise.reject(failedRequest);
};


createAuthRefreshInterceptor(gateway, refreshAuthLogic);

gateway.interceptors.request.use(async config => {
  const token = getToken();
  const tenant = localStorage.getItem('tenant');
  const profileCode = localStorage.getItem('crCodeProfile');
  if (token) {
    config.headers.Authorization = token;
  }
  if (tenant) {
    config.headers.Tenant = tenant;
  }

  if (profileCode && tenant !== 'ALL') {
    config.headers.ProfileCode = profileCode;
  }

  if (profileCode && tenant === 'ALL') {
    config.headers.ProfileCode = localStorage.getItem('tokenCrCode');
  }

  return config;
});

// const requestInterceptor = config => config;
const responseSuccessInterceptor = response => {
  delete endpointsTimeout[response.config.url];
  clearTimeout(timeout);
  const token = response.headers.authorization;
  if (token) {
    login(token);
  }

  return response.data;
};

const responseFailureInterceptor = async error => {
  // Verificar se error existe antes de acessar suas propriedades
  if (!error) {
    console.log('Erro undefined - não reportando para Bugsnag');
    return Promise.reject(new Error(UNKNOWN_REQUEST_ERROR_MESSAGE));
  }

  // Verificar se é um erro de cancelamento de requisição
  if (error.code === 'ERR_CANCELED' || 
      error.name === 'CanceledError' || 
      error.message === 'Request aborted' ||
      error.message === 'Solicitação cancelada') {
    // Para requisições canceladas, não reportar erro para Bugsnag
    console.log('Requisição cancelada - não reportando erro:', error.message);
    return Promise.reject(error);
  }

  // Verificar se é um erro 401 (Unauthorized) - não reportar para Bugsnag
  if (error.response && error.response.status === 401) {
    console.log('Erro 401 (Unauthorized) - não reportando para Bugsnag');
    return Promise.reject(error);
  }

  // Verificar se é um erro de rede ou timeout que pode estar chegando como undefined
  if (error.message && (
      error.message.includes('Network Error') ||
      error.message.includes('timeout') ||
      error.message.includes('401') ||
      error.message.includes('Unauthorized')
  )) {
    console.log('Erro de rede/timeout/401 - não reportando para Bugsnag:', error.message);
    return Promise.reject(error);
  }

  // Create a default error response if it doesn't exist
  const errorResponse = {
    data: {
      message: _.get(error, 'message', 'Solicitação cancelada')
    },
    status: 499,
    ...(error && error.response ? error.response : {})
  };

  // Ensure error.response exists
  if (!error) error = {}; // <-- Adicionado para garantir que error nunca é undefined
  error.response = errorResponse;

  try {
    const status = _.get(error, 'response.status', 499);

    if (status === 403) {
      logout();
      localStorage.setItem(
        'redirectExpired',
        'Acesso expirado, faça login novamente'
      );
      window.location.href = window.location.origin;
    }

    let errorMessage = _.get(error, 'response.data.message', 'API Error');
    if (!errorMessage && error.message) {
      errorMessage = error.message;
    }

    const errorToReport = new Error(errorMessage);
    errorToReport.name = 'API Error';
    errorToReport.stack = error.stack || new Error().stack;
    errorToReport.response = error.response;
    errorToReport.config = error.config;

    const requestHeaders = {
      ...(_.get(error, 'config.headers', {})),
      ...(_.get(error, 'response.headers', {})),
      'User-Agent': navigator.userAgent,
      'Referer': document.referrer,
      'URL': window.location.href
    };

    Bugsnag.notify(errorToReport, {
      context: 'API Error',
      metadata: {
        status: _.get(error, 'response.status', 499),
        url: _.get(error, 'config.url', ''),
        method: _.get(error, 'config.method', ''),
        data: _.get(error, 'response.data', {}),
        headers: requestHeaders,
        baseURL: _.get(error, 'config.baseURL', ''),
        originalError: error,
        request: {
          url: window.location.href,
          headers: requestHeaders,
          clientIp: _.get(error, 'response.headers.x-forwarded-for', '')
        }
      }
    });

    return Promise.reject(errorToReport);
  } catch (e) {
    console.log('erro sem response', e);
    
    let errorMessage = 'Unknown API Error';
    if (e.message) {
      errorMessage = e.message;
    } else if (typeof e === 'string') {
      errorMessage = e;
    }

    const errorToReport = new Error(errorMessage);
    errorToReport.name = 'API Error - No Response';
    errorToReport.stack = e.stack || new Error().stack;
    errorToReport.response = e.response;
    errorToReport.config = e.config;

    const requestHeaders = {
      'User-Agent': navigator.userAgent,
      'Referer': document.referrer,
      'URL': window.location.href
    };

    Bugsnag.notify(errorToReport, {
      context: 'API Error - No Response',
      metadata: {
        error: errorMessage,
        stack: e.stack,
        originalError: e,
        request: {
          url: window.location.href,
          headers: requestHeaders
        }
      }
    });

    return Promise.reject(errorToReport);
  }
};

gateway.interceptors.response.use(
  responseSuccessInterceptor,
  responseFailureInterceptor
);

export { gateway };
